import { useCallback, useMemo } from "react";

import { compareFractionalOrder } from "@/lib/compare-fractional-order";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { getFocusAreaListForWorkspaceQueryDocument } from "./focus-area.gql";

export interface UseGetFocusAreaListProps {
  workspaceId?: string;
  enabled?: boolean;
}

export const useGetFocusAreaList = ({ workspaceId, enabled = true }: UseGetFocusAreaListProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.focusAreas.list(workspaceId ?? ""),
    queryFn: async () => {
      if (!workspaceId) {
        throw new Error("Workspace ID is required");
      }
      const client = createOperationClient("GetFocusAreaListForWorkspace");
      const data = await client.request(getFocusAreaListForWorkspaceQueryDocument, {
        input: { workspaceId },
      });
      return data.focusAreaListForWorkspace;
    },
    enabled: enabled && !!workspaceId,
    staleTime: 2 * 60 * 1000, // 2 minutes for list data
  });

  const focusAreaList = useMemo(() => {
    const unsortedList = queryResult.data ?? [];
    return [...unsortedList].sort((a, b) => compareFractionalOrder(a.order, b.order));
  }, [queryResult.data]);

  const findFocusAreaById = useCallback(
    (id: string) => {
      return focusAreaList.find((p) => p.id === id);
    },
    [focusAreaList],
  );

  return {
    focusAreaList,
    loading: queryResult.isLoading,
    error: queryResult.error,
    queryResult,
    findFocusAreaById,
  };
};
